import React, { useContext, useState, useEffect, useCallback } from "react";
import {
  Spinner,
  Button,
  Modal,
  Breadcrumb,
  Tabs,
  Badge,
} from "flowbite-react";
import { Store } from "../context/store";
import { IoMdArrowBack, IoMdVideocam, IoMdPin } from "react-icons/io";
import {
  Bed,
  Bath,
  Maximize,
  Info,
  Navigation,
  History,
  CheckCircle2,
} from "lucide-react"; // Modern icon set
import { Link, useNavigate, useParams } from "react-router-dom";
import apiRequest from "../utils/apiRequest";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { useAllPermissions } from "../utils/usePermission";
import { confirmDialog } from "../components/ConfirmDialog";
import DashboardLayout from "../components/layout/DashboardLayout";

function SingleProperty() {
  let store = useContext(Store);
  let [baseUrl] = store.url;
  let [msg, setMsg] = useState("");
  let [loading, setLoading] = useState(false);
  let [project, setProject] = useState({});
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersCount, setSubscribersCount] = useState(0);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [openModalOut, setOpenModalOut] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const { canWriteProperty } = useAllPermissions();

  const loadProject = useCallback(async () => {
    setLoading(true);
    try {
      let token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/property/${id}`;
      const { data } = await apiRequest(url, "GET", null, token);
      setProject(data);
    } catch (error) {
      setMsg("Session Expired!");
      setOpenModalOut(true);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, id]);

  const loadSubscribers = useCallback(async () => {
    setSubscribersLoading(true);
    try {
      let token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/admin/property/${id}/subscribed-users`;
      const { data } = await apiRequest(url, "GET", null, token);
      setSubscribers(data?.users || []);
      setSubscribersCount(
        typeof data?.count === "number" ? data.count : data?.users?.length || 0,
      );
    } catch (error) {
      setSubscribers([]);
      setSubscribersCount(0);
    } finally {
      setSubscribersLoading(false);
    }
  }, [baseUrl, id]);

  useEffect(() => {
    loadProject();
    loadSubscribers();
  }, [loadProject, loadSubscribers]);

  const approveProperty = async () => {
    await confirmDialog(
      "Are you sure you want to approve this property?",
      {
        title: "Approve Property",
        confirmText: "Yes, approve",
        color: "success",
        loadingText: "Approving...",
        onConfirm: async () => {
          setLoading(true);
          try {
            let token = localStorage.getItem("royal-token");
            const url = `${baseUrl}/property/${id}`;
            await apiRequest(url, "PUT", { approved: true }, token);
            loadProject();
          } catch (error) {
            const unauthorizedErrors = [
              "Access Denied: Invalid or expired token. Please login again",
              "Access Denied: Please log in.",
              "Access Denied: Invalid Token.",
              "Access Denied: Invalid or expired token.",
              "Access Denied: Invalid or Expired Token.",
              "Access Denied: No token provided.",
            ];
            if (unauthorizedErrors.includes(error.message)) {
              setMsg("Session Expired!");
              setOpenModalOut(true);
              localStorage.removeItem("royal-token");
            } else {
              console.log(error);
            }
          } finally {
            setLoading(false);
          }
        },
      },
    );
  };

  return (
    <DashboardLayout active="projects" className="bg-[#F8FAFC]" contentClassName="p-4 md:p-8">
      <title>{project.title || "Loading..."} | Royalmines</title>

        <main>
          {/* Top Header & Breadcrumb */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
            <div>
              <Breadcrumb className="mb-4">
                <Breadcrumb.Item href="/properties" icon={Navigation}>
                  Properties
                </Breadcrumb.Item>
                <Breadcrumb.Item>{project?.title}</Breadcrumb.Item>
              </Breadcrumb>
              <h1 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight">
                {project.title}
              </h1>
              <div className="flex items-center text-gray-500 mt-2">
                <IoMdPin className="mr-1 text-blue-600" />
                <span className="text-sm font-medium">{project.address}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {!project.approved && (
                <Button
                  disabled={!canWriteProperty || loading}
                  color="success"
                  onClick={() => approveProperty()}
                  className="rounded-xl shadow-sm bg-green-600 hover:bg-green-700"
                >
                  Approve Property
                </Button>
              )}
              {/* <div className="text-right mr-4 hidden md:block">
                <p className="text-sm text-gray-500 font-bold uppercase tracking-wider">
                  Starting Price
                </p>
                <p className="text-2xl font-black text-blue-600">
                  ₦{project.price?.toLocaleString()}
                </p>
              </div> */}
              <Button
                color="light"
                onClick={() => navigate("/properties")}
                className="rounded-xl shadow-sm border-gray-200"
              >
                <IoMdArrowBack className="mr-2" /> Back
              </Button>
            </div>
          </div>

          {!loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column: Images & Details */}
              <div className="lg:col-span-2 space-y-8">
                {/* Modern Image Mosaic Gallery */}
                <div className="grid grid-cols-4 grid-rows-2 gap-3 h-[300px] md:h-[500px]">
                  <div className="col-span-4 md:col-span-3 row-span-2 overflow-hidden rounded-3xl bg-gray-200">
                    <img
                      src={project?.images?.[0]}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      alt="Main"
                    />
                  </div>
                  {project?.images?.[1] && (
                    <div className="hidden md:block col-span-1 row-span-1 overflow-hidden rounded-3xl bg-gray-200">
                      <img
                        src={project?.images?.[1]}
                        className="w-full h-full object-cover"
                        alt="Sub 1"
                      />
                    </div>
                  )}
                  {project?.images?.[2] && (
                    <div className="hidden md:block col-span-1 row-span-1 overflow-hidden rounded-3xl bg-gray-200 relative">
                      <img
                        src={project?.images?.[2]}
                        className="w-full h-full object-cover"
                        alt="Sub 2"
                      />
                      {project?.images?.length > 3 && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-bold text-xl">
                          +{project.images.length - 3}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Content Tabs */}
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
                  <Tabs.Group aria-label="Property Tabs" tabType="underline">
                    <Tabs.Item active title="Property Details" icon={Info}>
                      <div className="space-y-8 mt-6">
                        {/* Summary Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-6 bg-gray-50 rounded-2xl">
                          <div className="text-center md:border-r border-gray-200">
                            <p className="text-xs font-bold text-gray-400 uppercase">
                              Status
                            </p>
                            <Badge color="info" className="mt-1 inline-block">
                              {project.status}
                            </Badge>
                          </div>
                          <div className="text-center md:border-r border-gray-200">
                            <p className="text-xs font-bold text-gray-400 uppercase">
                              Approval
                            </p>
                            <Badge
                              color={project.approved ? "success" : "warning"}
                              className="mt-1 inline-block"
                            >
                              {project.approved ? "Approved" : "Pending"}
                            </Badge>
                          </div>
                          <div className="text-center md:border-r border-gray-200">
                            <p className="text-xs font-bold text-gray-400 uppercase">
                              Added On
                            </p>
                            <p className="font-bold text-gray-800">
                              {formatDate(project.createdAt)}
                            </p>
                          </div>
                          <div className="text-center md:border-r border-gray-200">
                            <p className="text-xs font-bold text-gray-400 uppercase">
                              Video
                            </p>
                            {project.videos?.[0] ? (
                              <a
                                href={project.videos[0]}
                                className="text-blue-600 flex items-center justify-center font-bold text-sm mt-1"
                              >
                                <IoMdVideocam className="mr-1" /> Virtual Tour
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </div>
                          <div className="text-center">
                            <p className="text-xs font-bold text-gray-400 uppercase">
                              Title
                            </p>
                            <p className="font-bold text-gray-800">
                              {project.documentType || "C of O"}
                            </p>
                          </div>
                        </div>

                        {/* Features Section */}
                        <div>
                          <h3 className="text-xl font-bold mb-4 flex items-center">
                            <CheckCircle2
                              className="mr-2 text-green-500"
                              size={20}
                            />{" "}
                            Premium Features
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {project.features?.map((f, i) => (
                              <span
                                key={i}
                                className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 shadow-sm"
                              >
                                {f}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </Tabs.Item>

                    <Tabs.Item title="Nearby Landmarks" icon={IoMdPin}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        {project.landmarks?.map((l, i) => (
                          <div
                            key={i}
                            className="flex items-center p-4 bg-gray-50 rounded-xl"
                          >
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                              <Navigation size={16} className="text-blue-600" />
                            </div>
                            <span className="font-medium text-gray-700">
                              {l}
                            </span>
                          </div>
                        ))}
                      </div>
                    </Tabs.Item>
                  </Tabs.Group>
                </div>
              </div>

              {/* Right Column: Units & Action */}
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-3xl shadow-xl border border-gray-100 sticky top-24">
                  <h3 className="text-xl font-bold mb-6">Available Units</h3>

                  <div className="space-y-4">
                    {project.propertyUnits?.map((unit, idx) => (
                      <div
                        key={idx}
                        className="group p-4 rounded-2xl border border-gray-100 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/30 transition-all cursor-pointer"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="font-bold text-gray-900">
                            {unit.unit}
                          </h4>
                          <span className="text-blue-700 font-black">
                            ₦{unit.price?.toLocaleString()}
                          </span>
                        </div>

                        <div className="grid grid-cols-3 gap-2 text-[10px] font-bold text-gray-500 uppercase">
                          <div className="flex items-center">
                            <Bed size={14} className="mr-1" /> {unit.bedrooms}{" "}
                            Bedroom
                          </div>
                          <div className="flex items-center">
                            <Bath size={14} className="mr-1" /> {unit.bathroom}{" "}
                            Bathroom
                          </div>
                          <div className="flex items-center">
                            <Maximize size={14} className="mr-1" />{" "}
                            {unit.propertySize}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* <Button className="w-full mt-8 bg-blue-600 hover:bg-blue-700 py-3 rounded-2xl font-bold shadow-lg shadow-blue-200">
                    Reserve a Unit
                  </Button> */}
                </div>

                <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
                  <h3 className="font-bold mb-4 flex items-center uppercase text-xs tracking-widest text-gray-400">
                    <History size={16} className="mr-2" /> Subscribed Users
                  </h3>

                  <div className="mb-4">
                    <Badge color="info" className="inline-block">
                      {subscribersCount} Subscriber
                      {subscribersCount === 1 ? "" : "s"}
                    </Badge>
                  </div>

                  {subscribersLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Spinner size="sm" /> Loading subscribers...
                    </div>
                  ) : subscribers.length ? (
                    <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                      {subscribers.map((subscription, i) => {
                        const client = subscription?.client || {};
                        const clientId = client?.id || client?._id;
                        const unitSnapshot = subscription?.unitSnapshot || {};
                        const fullName =
                          `${client?.firstName || ""} ${client?.lastName || ""}`.trim() ||
                          client?.fullName ||
                          client?.name ||
                          "Unnamed user";

                        return (
                          <div
                            key={
                              subscription?.subscriptionId || client?.id || i
                            }
                            className="p-3 rounded-xl border border-gray-100 bg-gray-50"
                          >
                            <p className="font-semibold text-sm text-gray-800">
                              {fullName}
                            </p>
                            <p className="text-xs text-gray-500 break-all">
                              {client?.email || "No email"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {client?.phone || "No phone"}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              Unit: {unitSnapshot?.unit || "N/A"}
                            </p>
                            <p className="text-xs text-gray-600">
                              Amount: ₦
                              {subscription?.amount?.toLocaleString?.() || "0"}
                            </p>
                            <p className="text-xs text-gray-600 capitalize">
                              Status: {subscription?.status || "N/A"}
                            </p>
                            {clientId && (
                              <Link
                                to={`/client/${clientId}`}
                                className="inline-block mt-2 text-xs font-semibold text-blue-600 hover:text-blue-700"
                              >
                                View client profile
                              </Link>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      No subscribed users for this property.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center h-[60vh] space-y-4">
              <Spinner size="xl" />
              <p className="text-gray-400 font-medium animate-pulse">
                Fetching property details...
              </p>
            </div>
          )}
        </main>

      <Modal
        show={openModalOut}
        size="md"
        popup
        onClose={() => setOpenModalOut(false)}
      >
        <Modal.Header />
        <Modal.Body className="text-center">
          <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-red-500" />
          <h3 className="text-lg font-bold text-gray-900 mb-6">{msg}</h3>
          <Button
            color="failure"
            className="w-full rounded-xl"
            onClick={() => navigate("/")}
          >
            Login Again
          </Button>
        </Modal.Body>
      </Modal>
    </DashboardLayout>
  );
}

export default SingleProperty;

function formatDate(dateString) {
  if (!dateString) return "N/A";
  const dateObj = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(dateObj);
}
